
import React, { useEffect, useState, useRef, useCallback } from 'react';

interface LearnProps {
  currentLevel: number;
  currentLanguage: string;
  translate: boolean;
  lessons: Lesson[];
  initialIndex?: number;
  onNext?: () => void;
  onPrevious?: () => void;
}

interface Lesson {
  id: string;
  level: number;
  order: number;
  content: ContentItem[];
}

interface ContentItem {
  text?: string;
  translation?: Record<string, string>;
  html?: string;
  examples?: ExampleItem[];
}

interface ExampleItem {
  text: string;
  img: string;
  translation: Record<string, string>;
}

const Learn: React.FC<LearnProps> = ({
  currentLevel,
  currentLanguage,
  translate,
  lessons,
  initialIndex,
  onNext,
  onPrevious
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex ?? 0);
  const [isListening, setIsListening] = useState(false);
  const [feedback, setFeedback] = useState<string>("");
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const [currentExampleIndex, setCurrentExampleIndex] = useState<number | null>(null);
  const recognitionRef = useRef<any>(null);

  // Minimum swipe distance (in px)
  const minSwipeDistance = 50;

  // Reset index when level or initialIndex changes
  useEffect(() => {
    // Clamp the initial index to the available lessons to avoid out-of-range errors
    if (!lessons || lessons.length === 0) {
      setCurrentIndex(0);
    } else {
      const desired = initialIndex ?? 0;
      const clamped = Math.min(Math.max(0, desired), lessons.length - 1);
      setCurrentIndex(clamped);
    }
    setFeedback("");
    setCurrentExampleIndex(null);
  }, [currentLevel, initialIndex, lessons.length]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') {
        goToNext();
      } else if (e.key === 'ArrowLeft') {
        goToPrevious();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, lessons.length, onNext, onPrevious]);

  const goToNext = () => {
    if (currentIndex < lessons.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setFeedback("");
      setCurrentExampleIndex(null);
    } else if (onNext) {
      onNext();
    }
  };

  const goToPrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setFeedback("");
      setCurrentExampleIndex(null);
    } else if (onPrevious) {
      onPrevious();
    }
  };

  // Touch handlers for swipe
  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      goToNext();
    } else if (isRightSwipe) {
      goToPrevious();
    }
  };

  // Text-to-speech
  const speakText = (text: string) => {
    if ('speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = 'en-US';
      window.speechSynthesis.speak(utterance);
    }
  };

  // Speech recognition
  const startListening = useCallback((targetText: string, exampleIdx: number) => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      alert('Speech recognition not supported in this browser');
      return;
    }

    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.lang = 'en-US';
    recognition.continuous = false;
    recognition.interimResults = false;

    recognition.onstart = () => {
      setIsListening(true);
      setCurrentExampleIndex(exampleIdx);
      setFeedback("Listening...");
    };

    recognition.onresult = (event: any) => {
      const transcript = event.results[0][0].transcript.toLowerCase().trim();
      const target = targetText.toLowerCase().trim();

      if (transcript === target || transcript.replace(/[.,!?]/g, '') === target.replace(/[.,!?]/g, '')) {
        setFeedback(`✓ Correct! It is "${targetText}"`);
        speakText(`Correct! It is ${targetText}`);
      } else {
        setFeedback(`✗ You said: "${transcript}". Try again!`);
      }
      setIsListening(false);
      setCurrentExampleIndex(null);
    };

    recognition.onerror = (event: any) => {
      console.error('Speech recognition error', event.error);
      setIsListening(false);
      setCurrentExampleIndex(null);
      setFeedback("Error with speech recognition. Try again.");
    };

    recognition.onend = () => {
      setIsListening(false);
      setCurrentExampleIndex(null);
    };

    recognitionRef.current = recognition;
    recognition.start();
  }, []);

  const stopListening = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      setIsListening(false);
      setCurrentExampleIndex(null);
    }
  };

  if (lessons.length === 0) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        color: 'white',
        fontSize: '1.5rem',
        textShadow: '0 2px 4px rgba(0,0,0,0.5)'
      }}>
        No lessons available for this level.
      </div>
    );
  }

  const currentLesson = lessons[currentIndex];

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        minHeight: '100vh',
        width: '100%',
        padding: 'clamp(10px, 2vh, 20px)',
        paddingTop: 'clamp(60px, 10vh, 100px)', // Top margin for parent buttons
        position: 'relative',
        backgroundColor: 'transparent',
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch'
      }}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      {/* Main Card */}
      <div style={{
        maxWidth: '800px',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 'clamp(6px, 1.25vh, 12px)',
        margin: 'auto'
      }}>

        {/* Content Items */}
        {currentLesson.content.map((item, idx) => (
          <div key={idx}>
            {/* Text Content */}
            {item.text && (
              <div style={{
                backgroundColor: 'rgba(0,0,0,0.25)',
                backdropFilter: 'blur(10px)',
                padding: 'clamp(8px, 1.5vh, 16px)',
                borderRadius: '12px',
                textAlign: 'center'
              }}>
                <p style={{
                  color: 'white',
                  fontSize: 'clamp(1rem, 2.5vw, 1.5rem)',
                  margin: 0,
                  lineHeight: '1.6',
                  textShadow: '0 2px 4px rgba(0,0,0,0.5)'
                }}>
                  {item.translation && (item.translation as Record<string, string>)[currentLanguage] && (
                    <h3 style={{
                      color: 'white',
                      fontSize: 'clamp(1.1rem, 4vh, 2rem)',
                      margin: '0 0 8px 0',
                      textShadow: '0 2px 4px rgba(0,0,0,0.5)'
                    }}>
                      {(item.translation as Record<string, string>)[currentLanguage]}
                    </h3>
                  )}</p>
              </div>
            )}

            {/* HTML Content */}
            {item.html && (
              <div style={{
                backgroundColor: 'rgba(0,0,0,0.25)',
                backdropFilter: 'blur(10px)',
                padding: 'clamp(16px, 3vh, 32px)',
                borderRadius: '12px'
              }}>
                <div
                  style={{
                    color: 'white',
                    fontSize: 'clamp(1rem, 2.5vw, 1.5rem)',
                    lineHeight: '1.6',
                    textShadow: '0 2px 4px rgba(0,0,0,0.5)'
                  }}
                  dangerouslySetInnerHTML={{ __html: item.html }}
                />
              </div>
            )}

            {/* Examples Content */}
            {item.examples && item.examples.length > 0 && (
              <div style={{
                display: 'flex',
                flexDirection: 'row',
                gap: 'clamp(16px, 3vh, 32px)',
                justifyContent: 'center',
                alignItems: 'center'
              }}>
                {item.examples.map((example, exampleIdx) => (
                  <div key={exampleIdx} style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 'clamp(4px, 1vh, 8px)'
                  }}>
                    {/* Image */}
                    <div className="example-image-container">
                      <img
                        src={example.img}
                        alt={example.text}
                        className="example-image"
                      />
                    </div>

                    {/* Text and Translation */}
                    <div style={{
                      backgroundColor: 'rgba(0,0,0,0.25)',
                      backdropFilter: 'blur(10px)',
                      padding: 'clamp(6px, 1.25vh, 12px)',
                      borderRadius: '12px',
                      textAlign: 'center'
                    }}>
                      <h3 style={{
                        color: 'white',
                        fontSize: 'clamp(1.1rem, 4vh, 2rem)',
                        margin: '0 0 8px 0',
                        textShadow: '0 2px 4px rgba(0,0,0,0.5)'
                      }}>
                        {example.text}
                      </h3>

                      {translate && example.translation[currentLanguage] && (
                        <p style={{
                          color: 'rgba(255,255,255,0.75)',
                          fontSize: 'clamp(0.9rem, 2.2vw, 1.15rem)',
                          margin: 0,
                          fontStyle: 'italic',
                          textShadow: '0 2px 4px rgba(0,0,0,0.5)'
                        }}>
                          {example.translation[currentLanguage]}
                        </p>
                      )}
                    </div>

                    {/* Controls for Example */}
                    <div style={{
                      display: 'flex',
                      gap: '12px',
                      justifyContent: 'center',
                      flexWrap: 'wrap'
                    }}>
                      <button
                        onClick={() => speakText(example.text)}
                        style={{
                          padding: 'clamp(6px, 1.5vh, 12px) clamp(10px, 3vw, 24px)',
                          fontSize: 'clamp(0.8rem, 1.5vh, 1rem)',
                          backgroundColor: 'rgba(59, 130, 246, 0.7)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          backdropFilter: 'blur(10px)',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                          transition: 'transform 0.2s',
                          fontWeight: '600',
                          whiteSpace: 'nowrap'
                        }}
                        onMouseDown={(e) => e.currentTarget.style.transform = 'scale(0.95)'}
                        onMouseUp={(e) => e.currentTarget.style.transform = 'scale(1)'}
                      >
                        🔊 Play
                      </button>

                      <button
                        onClick={
                          isListening && currentExampleIndex === exampleIdx
                            ? stopListening
                            : () => startListening(example.text, exampleIdx)
                        }
                        style={{
                          padding: 'clamp(6px, 1.5vh, 12px) clamp(10px, 3vw, 24px)',
                          fontSize: 'clamp(0.8rem, 1.5vh, 1rem)',
                          backgroundColor:
                            isListening && currentExampleIndex === exampleIdx
                              ? 'rgba(239, 68, 68, 0.7)'
                              : 'rgba(16, 185, 129, 0.7)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          backdropFilter: 'blur(10px)',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                          transition: 'transform 0.2s',
                          fontWeight: '600',
                          whiteSpace: 'nowrap'
                        }}
                        onMouseDown={(e) => e.currentTarget.style.transform = 'scale(0.95)'}
                        onMouseUp={(e) => e.currentTarget.style.transform = 'scale(1)'}
                      >
                        {isListening && currentExampleIndex === exampleIdx ? '⏹️ Stop' : '🎤 Speak'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}

        {/* Feedback */}
        {feedback && (
          <div style={{
            backgroundColor: 'rgba(0,0,0,0.4)',
            backdropFilter: 'blur(10px)',
            padding: 'clamp(8px, 1.5vh, 16px)',
            borderRadius: '8px',
            textAlign: 'center',
            color: 'white',
            fontSize: 'clamp(0.9rem, 1.8vh, 1.1rem)'
          }}>
            {feedback}
          </div>
        )}

        {/* Navigation */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '12px',
          marginTop: 'clamp(8px, 2vh, 16px)'
        }}>
          <button
            onClick={goToPrevious}
            disabled={currentIndex === 0 && !onPrevious}
            style={{
              padding: 'clamp(6px, 1.5vh, 12px) clamp(10px, 2vw, 20px)',
              fontSize: 'clamp(0.8rem, 1.5vh, 1rem)',
              backgroundColor: (currentIndex === 0 && !onPrevious) ? 'rgba(100,100,100,0.4)' : 'rgba(100,100,100,0.7)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: (currentIndex === 0 && !onPrevious) ? 'not-allowed' : 'pointer',
              backdropFilter: 'blur(10px)',
              fontWeight: '600',
              whiteSpace: 'nowrap'
            }}
          >
            ← Previous
          </button>

          <span style={{
            color: 'white',
            fontSize: '1rem',
            textShadow: '0 2px 4px rgba(0,0,0,0.5)',
            backgroundColor: 'rgba(0,0,0,0.25)',
            padding: '8px clamp(8px, 2vw, 16px)',
            borderRadius: '8px',
            backdropFilter: 'blur(10px)',
            whiteSpace: 'nowrap'
          }}>
            {currentIndex + 1} / {lessons.length}
          </span>

          <button
            onClick={goToNext}
            disabled={currentIndex === lessons.length - 1 && !onNext}
            style={{
              padding: 'clamp(6px, 1.5vh, 12px) clamp(10px, 2vw, 20px)',
              fontSize: 'clamp(0.8rem, 1.5vh, 1rem)',
              backgroundColor: (currentIndex === lessons.length - 1 && !onNext) ? 'rgba(100,100,100,0.4)' : 'rgba(100,100,100,0.7)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: (currentIndex === lessons.length - 1 && !onNext) ? 'not-allowed' : 'pointer',
              backdropFilter: 'blur(10px)',
              fontWeight: '600',
              whiteSpace: 'nowrap'
            }}
          >
            Next →
          </button>
        </div>
      </div>
    </div>
  );
};

export default Learn;
